<template>
  <div>

    <!-- Page Header -->
    <div class="page-header">
      <div>
        <button type="button" @click="$router.go(-1)" class="btn-back">
          <font-awesome-icon icon="arrow-left" />
        </button>
        <h5 class="page-title mt-2">
          Invoice
          <span v-if="invoice?.invoice_no" class="batch-pill ms-2">#{{ invoice.invoice_no }}</span>
        </h5>
        <p class="page-sub" v-if="invoice?.customer?.customer_name">{{ invoice.customer.customer_name }}</p>
      </div>
      <div class="page-actions" v-if="!loading">

        <button class="btn-outline" @click="downloadPDF">
          <font-awesome-icon icon="file-pdf" class="me-2 text-danger" />
          Download PDF
        </button>

        <router-link
          v-if="invoice?.status == 0"
          :to="$url('/invoice/update/' + invoice?.id)"
          class="btn-outline"
        >
          <font-awesome-icon icon="pen" class="me-2" /> Edit Invoice
        </router-link>

        <router-link
          v-if="invoice?.status != 2"
          :to="$url('/invoice/record-payment/' + invoice?.id)"
          class="btn-primary-action"
        >
          <font-awesome-icon icon="plus" /> Record Payment
        </router-link>

      </div>
    </div>

    <!-- Loading -->
    <div v-if="loading" class="invoice-shell">
      <div class="skeleton skeleton--header mb-4"></div>
      <div class="skeleton-row mb-4">
        <div class="skeleton skeleton--block"></div>
        <div class="skeleton skeleton--block"></div>
        <div class="skeleton skeleton--block"></div>
      </div>
      <div class="skeleton skeleton--table mb-3"></div>
      <div class="skeleton skeleton--totals ms-auto"></div>
    </div>

    <!-- Invoice -->
    <div v-else class="invoice-shell" id="printMe">
      <Invoice
        :invoice_items="invoice_items"
        :invoice_array="invoice_array"
        :invoice="invoice"
        :loading="loading"
      />
    </div>

  </div>
</template>

<script>
import { useToast } from 'vue-toastification';
import jsPDF        from 'jspdf';
import html2canvas  from 'html2canvas';
import { reusableMixin } from '../../../lib/reusableMixin';
import { useStore } from 'vuex';
import Invoice from './component/invoice';
import axios from 'axios';

export default {
  components: { Invoice },
  mixins: [reusableMixin],

  data() {
    return {
      toast:         useToast(),
      store:         useStore(),
      invoice:       null,
      invoice_items: [],
      invoice_array: [],
      loading:       true,
    };
  },

  created() { this.getInvoice(); },

  methods: {
    getInvoice() {
      this.loading = true;
      axios.get(`/invoice/detail/${this.$route.params.id}`)
        .then(response => {
          this.loading       = false;
          this.invoice       = response.data.data;
          this.invoice_items = response.data.data.items ?? [];
          this.invoice_array = this.invoice_items.map(s => ({
            description: s?.description,
            quantity:    s?.quantity,
            price:       s?.price,
            apply_tax:   s?.apply_tax == 1,
            tax_amount:  parseFloat(s?.tax_amount ?? 0),
            total:       parseFloat(s?.total ?? 0),
          }));
        })
        .catch(() => {
          this.loading = false;
          this.toast.error('Could not load invoice');
        });
    },

    async downloadPDF() {
      const el     = document.getElementById('printMe');
      const canvas = await html2canvas(el, { scale: 2, useCORS: true });
      const pdf    = new jsPDF('p', 'mm', 'a4');
      const w      = pdf.internal.pageSize.getWidth();
      const h      = (canvas.height * w) / canvas.width;
      pdf.addImage(canvas.toDataURL('image/png'), 'PNG', 0, 0, w, h);
      pdf.save(`Invoice_${this.invoice?.invoice_no}_${this.formatDateMMDDYY()}.pdf`);
    },
  },
};
</script>

<style scoped>
.page-header  { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:14px; margin-bottom:20px; }
.page-title   { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
.page-sub     { font-size:13px; color:#7a90b0; margin:0; }
.page-actions { display:flex; align-items:center; flex-wrap:wrap; gap:8px; }
.btn-back     { display:inline-flex; align-items:center; gap:7px; background:transparent; border:none; color:#7a90b0; font-size:13px; cursor:pointer; padding:0; margin-bottom:4px; }
.btn-back:hover { color:#1a2b45; }
.batch-pill   { display:inline-block; background:#edf4ff; color:#004AAD; border:1px solid #c5d6f5; border-radius:20px; padding:2px 12px; font-size:13px; font-weight:600; }
.ms-2 { margin-left:8px; } .me-2 { margin-right:7px; } .mt-2 { margin-top:8px; }

.btn-primary-action { display:inline-flex; align-items:center; gap:7px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:8px 16px; border-radius:8px; border:none; text-decoration:none; cursor:pointer; transition:background .15s; white-space:nowrap; }
.btn-primary-action:hover { background:#003b8e; color:#fff; text-decoration:none; }
.btn-outline  { display:inline-flex; align-items:center; gap:7px; background:#fff; color:#1a2b45; border:1px solid #dde5f0; font-size:13px; font-weight:500; padding:8px 14px; border-radius:8px; cursor:pointer; transition:background .15s; text-decoration:none; white-space:nowrap; }
.btn-outline:hover { background:#f4f7fb; text-decoration:none; }

/* ── Skeleton ── */
.skeleton      { border-radius:8px; background:linear-gradient(90deg,#f0f4f8 25%,#e4eaf2 50%,#f0f4f8 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }
.skeleton--header  { height:110px; border-radius:12px 12px 0 0; }
.skeleton--block   { height:120px; flex:1; border-radius:8px; }
.skeleton--table   { height:200px; }
.skeleton--totals  { height:120px; width:280px; }
.skeleton-row { display:flex; gap:16px; }
.mb-3 { margin-bottom:12px; } .mb-4 { margin-bottom:16px; }
.ms-auto { margin-left:auto; }

/* ── Invoice Shell ── */
.invoice-shell { max-width:880px; margin:0 auto; }

/* ── Responsive ── */
@media(max-width:767px) {
  .page-header,.page-actions { flex-direction:column; align-items:stretch; }
  .invoice-shell { max-width:100%; }
}

@media print {
  .page-header { display:none !important; }
  .invoice-shell { max-width:100%; }
}
</style>